import './styles.css';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Modal from 'react-modal';
import * as Lucide from "lucide-react";
import { NAV_BAR_HEIGHT_PX } from '@mental-mint/shared-utils';
import { RootState } from '../Store/store';
import { playSound, SharedSoundEffect } from '../Sounds/SoundPlayer';

// Define constant for the modal transition duration (in milliseconds)
const MODAL_TRANSITION_DURATION_MS = 300;

// Set CSS custom property so the same value is used in CSS
document.documentElement.style.setProperty(
  '--modal-transition-duration',
  `${MODAL_TRANSITION_DURATION_MS}ms`
);

interface ModalStylizedProps {
  isOpen: boolean;
  onRequestClose?: () => void; // Optional: if not provided, close button won't show
  children?: React.ReactNode;
  iconImage?: string;
  title?: string;
  subtitle?: string;
  hasNavBar?: boolean; // Whether game has a navbar (affects mobile height calculation)
}

interface ModalStylizedState { 
  isNarrowScreen: boolean,
}

const NARROW_SCREEN_CUTOFF_WIDTH_PX = 768;

const ModalStylized: React.FC<ModalStylizedProps> = ({
  isOpen,
  onRequestClose,
  children,
  iconImage,
  title,
  subtitle,
  hasNavBar = true
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  
  const getStateScreenDimensions = () => {
    // TODO: yes, media queries could work better. Fix this if you dare.
    return { 
      isNarrowScreen: window.innerWidth < NARROW_SCREEN_CUTOFF_WIDTH_PX,
    };
  };

  const [screenState, setScreenState] = useState<ModalStylizedState>(getStateScreenDimensions());
  
  useEffect(() => {
    const handleResize = () => {
      setScreenState(getStateScreenDimensions());
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderCloseButton = () => {
    return (
      <div className="modal-close-button" onClick={() => {
        playSound(SharedSoundEffect.MENU_CLICK);
        onRequestClose?.();
      }} 
      style={{
        position: "absolute",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        top: "5px",
        right: "5px",
        cursor: "pointer",
        backgroundColor: closeButtonBgColor,
        borderRadius: "100%",
        width: "37px",
        height: "37px",
        margin: "13px",
        color: closeButtonTextColor,
      }}
    >
      <Lucide.ChevronUp size={30} />
    </div>
    );
  };

  const renderTitles = () => {
    const { isNarrowScreen } = screenState;
    
    if (!currentGame) {
      return null; // Fallback if Redux isn't initialized
    }
    
    return (
      <div style={{paddingTop: "2rem", display: "flex", flexDirection: "column", alignItems: "center"}}>
        {iconImage && (
          <img src={iconImage} alt="Game icon" style={{marginBottom:"1rem", width:"30%"}}/>
        )}
        {title && (
          <p style={{fontSize: isNarrowScreen ? "2rem" : "2.5rem", margin: "0", color: titleTextColor, fontFamily: currentGame.fontPrimary}}>
              {title}
          </p>
        )}
        {subtitle && (
          <p style={{fontSize:"1.5rem", margin: "0", marginBottom:'1rem', color: subtitleTextColor, fontFamily: currentGame.fontSecondary}}>
              {subtitle}
          </p>
        )}
      </div>
    );  
  };

  const { isNarrowScreen } = screenState;

  // Calculate top position based on navigation setup
  const topPositionPx = isNarrowScreen ? '0' : '25';
  // If no navbar, modal should take full height on mobile
  const navBarHeightForCalc = hasNavBar ? NAV_BAR_HEIGHT_PX : 0;
  const narrowScreenHeight = `calc(100dvh - ${navBarHeightForCalc}px)`;

  // Use game's colorUiBackground if available, otherwise default dark background
  const modalBackgroundColor = currentGame?.colorUiBackground || 'rgba(20, 20, 20, 1)';
  
  // Determine text colors based on whether we're using light or dark background
  const isLightBackground = currentGame?.colorUiBackground ? true : false;
  const titleTextColor = isLightBackground ? currentGame?.colorPrimary || '#333' : 'white';
  const subtitleTextColor = isLightBackground ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.5)';
  const closeButtonBgColor = isLightBackground ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.15)';
  const closeButtonTextColor = isLightBackground ? '#333' : 'white';

  return (
    <div>
      <Modal 
         isOpen={isOpen}
         contentLabel="A modal"
         onRequestClose={onRequestClose}
         shouldCloseOnOverlayClick={true}
         closeTimeoutMS={MODAL_TRANSITION_DURATION_MS}
         bodyOpenClassName="modal-open"
         style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            zIndex: 10000,
          },
          content: {
            backgroundColor: modalBackgroundColor,
            borderRadius: isNarrowScreen ? '0' : '7px',
            boxShadow: '1px 10px 15px black',
            border: 'none',
            width: isNarrowScreen ? '100dvw' : '600px',
            maxWidth: '100dvw',
            height: isNarrowScreen ? narrowScreenHeight : 'fit-content',
            maxHeight: isNarrowScreen ? narrowScreenHeight : `calc(100dvh - (${navBarHeightForCalc}px + ${topPositionPx}px + 20px))`,
            minHeight: '300px',
            paddingBottom: '0',
            boxSizing: 'border-box',
            position: 'absolute',
            top: `${topPositionPx}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10001,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflowX: 'auto'
          }
         }}
      >
        {onRequestClose && renderCloseButton()}
        {renderTitles()}
        <div style={{overflowY: 'auto', width:'100%', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
          {children}
        </div>
      </Modal>
    </div>
  );
};

export default ModalStylized;